import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import ToolTip from "components/design/toolTip";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { NoNoteAvailable } from "components/DesignSystem/Notes/NoNoteAvailable";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { ArrowRightUpV2 } from "components/icons/ArrowUpRightV2";
import { Pin } from "components/icons/Chat/Pin";
import { Pinned } from "components/icons/Pinned";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import {
  MENTION_LIST_CONTAINER_CLASS,
  NOTE_TAB,
  NOTE_TYPE,
  NoteTabType,
  NoteType,
  SOURCE,
  SourceType,
} from "constants/unifyNotes";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useToast } from "hooks/useToast";
import Quill from "quill";
import type { MentionCandidate } from "../QuillEditor/QuillEditor";
import React, { Suspense, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  AllNote,
  groupNotesResponse,
  Note,
  NoteResponse,
  UnifyNotesApiResponse,
  useAddGroupNotesMutation,
  useCreateNotesMutation,
  useLazyGetAllUnifyNotesQuery,
  useLazyGetNoteQuery,
  useLazyGroupNotesQuery,
  usePinNoteMutation,
} from "store/apis/notes";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { BackendError } from "types/utils/error";
import { formatDate } from "utils/formatDate";
import { extractMentionedProfileIds } from "util/extractMentionedProfileIds";
import { EditNote } from "./EditNote";
import { TicketNoteCard } from "./TicketNoteCard";
import { UnifyNoteOptions } from "./UnifyNoteOptions";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";

const QuillEditor = React.lazy(() => import("../QuillEditor/QuillEditor"));

// The mention dropdown is portaled to document.body (outside the Modal's own
// DOM subtree) so it can escape the Modal's clipped bounds. Radix treats a
// click there as an "outside" interaction and would otherwise close the Modal.
const handleMentionListPointerDownOutside = (
  event: CustomEvent<{ originalEvent: PointerEvent }>
) => {
  const target = event.detail.originalEvent.target as HTMLElement | null;
  if (target?.closest(`.${MENTION_LIST_CONTAINER_CLASS}`)) {
    event.preventDefault();
  }
};

type UnifyNotesProps = {
  isOpen: boolean;
  close: () => void;
  currentNoteLabel: string;
  showSource?: boolean;
  groupId: string;
  currentNoteType: NoteType;
  entityId?: string;
  currentNoteTypeId: string;
  ticketAssignees?: { assignee_name: string; assignee_uuid: string }[];
  ticketResolved?: boolean;
};

type NoteProps<T> = {
  notes: T[];
  setPagination: () => void;
  hasMore: boolean;
  isLoading: boolean;
  handlePinUpdate: (noteId: string, isPinned: boolean) => () => void;
  resetNotes: () => void;
  isArchived: boolean;
  editableNoteId: string | null;
  setEditableNoteId: React.Dispatch<React.SetStateAction<string | null>>;
  isPinning: boolean;
  pinningNoteId: string | null;
  currentNoteTypeId?: string;
};

export const RenderDescription = ({
  description,
  quillRefItem,
}: {
  description: string;
  quillRefItem: React.RefObject<Quill | undefined | null>;
}) => {
  let formattedNotes = "";
  try {
    formattedNotes = JSON.parse(description);
  } catch {
    formattedNotes = description;
  }

  if (typeof formattedNotes === "object" && formattedNotes !== null) {
    return (
      <QuillEditor
        initReadOnly
        theme="bubble"
        defaultValue={formattedNotes}
        parentRef={quillRefItem}
        onTextChange={() => {}}
      />
    );
  }

  return (
    <span className="t-text-body-sm t-text-text-100 t-break-words t-whitespace-pre-line">
      {formattedNotes}
    </span>
  );
};

export const NoteActions = ({
  noteId,
  isPinned,
  handlePinUpdate,
  isArchived,
  setEditableNoteId,
  resetNotes,
  isPinning,
  isPinnedNote,
  noteCreatedBy,
}: {
  noteId: string;
  isPinned: boolean;
  handlePinUpdate: (noteId: string, isPinned: boolean) => () => void;
  isArchived: boolean;
  setEditableNoteId: React.Dispatch<React.SetStateAction<string | null>>;
  resetNotes: () => void;
  isPinning: boolean;
  isPinnedNote: boolean;
  noteCreatedBy: string | undefined;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className="t-flex t-items-center t-gap-1.5 t-h-10 t-relative">
      {isPinning ? (
        <Loader size="small" />
      ) : (
        <div>
          {isPinned ? (
            <ToolTip text="Remove pin">
              <div className="t-text-purple-70">
                <Button
                  customType="ghost"
                  onClick={handlePinUpdate(noteId, false)}
                >
                  <Pin />
                </Button>
              </div>
            </ToolTip>
          ) : (
            <ToolTip text="Pin note">
              <div
                className={classNames(
                  "t-text-text-30 t-opacity-0 group-hover:t-opacity-100",
                  {
                    "t-opacity-100": isDropdownOpen,
                  }
                )}
              >
                <Button
                  customType="ghost"
                  onClick={handlePinUpdate(noteId, true)}
                >
                  <Pinned size="16" />
                </Button>
              </div>
            </ToolTip>
          )}
        </div>
      )}
      <UnifyNoteOptions
        noteId={noteId}
        isArchived={isArchived}
        handleEdit={(noteId: string) => setEditableNoteId(noteId)}
        resetNotes={resetNotes}
        onDropdownOpenChange={setIsDropdownOpen}
        isDropdownOpen={isDropdownOpen}
        isPinnedNote={isPinnedNote}
        noteCreatedBy={noteCreatedBy}
      />
    </div>
  );
};

export const RenderNotes = ({
  notes,
  setPagination,
  hasMore,
  isLoading,
  handlePinUpdate,
  currentNoteTypeId,
  resetNotes,
  isArchived,
  editableNoteId,
  setEditableNoteId,
  isPinning,
  pinningNoteId,
  ticketAssignees,
  ticketResolved,
  mentionCandidates,
}: NoteProps<Note> & {
  currentNoteTypeId: string;
  ticketAssignees?: { assignee_name: string; assignee_uuid: string }[];
  ticketResolved?: boolean;
  mentionCandidates?: MentionCandidate[];
}) => {
  const quillRefItem = useRef(null);

  if (!currentNoteTypeId)
    return (
      <Banner
        title="Notes unavailable"
        description="This task hasn’t been launched yet. Adding or viewing notes will be available once it’s launched."
        type="info"
      />
    );

  if (isLoading && notes.length === 0)
    return (
      <div className="t-flex t-justify-center t-items-center t-h-32">
        <Loader size="small" />
      </div>
    );

  return notes.length === 0 ? (
    <NoNoteAvailable />
  ) : (
    <InfiniteScroll
      dataLength={notes.length}
      next={setPagination}
      hasMore={true}
      loader={
        isLoading ? (
          <div className="t-flex t-justify-center t-items-center t-my-4">
            <Loader size="small" />
          </div>
        ) : null
      }
      scrollableTarget="scrollableNotesDiv"
    >
      <div className="t-space-y-4">
        {notes.map((note, index) => {
          if (note.uuid === editableNoteId) {
            return (
              <EditNote
                noteId={note.uuid}
                description={note.description}
                setEditableNoteId={setEditableNoteId}
                key={note.uuid}
                resetNotes={resetNotes}
                mentionCandidates={mentionCandidates}
              />
            );
          }
          return (
            <TicketNoteCard
              key={note.description + note.updated_at}
              note={note}
              quillRefItem={quillRefItem}
              handlePinUpdate={handlePinUpdate}
              isArchived={isArchived}
              setEditableNoteId={setEditableNoteId}
              resetNotes={resetNotes}
              isPinning={isPinning}
              pinningNoteId={pinningNoteId}
              ticketAssignees={ticketAssignees}
              ticketResolved={ticketResolved}
            />
          );
        })}
      </div>
    </InfiniteScroll>
  );
};

export const RenderAllNotes = ({
  notes,
  setPagination,
  hasMore,
  isLoading,
  handlePinUpdate,
  resetNotes,
  isArchived,
  editableNoteId,
  setEditableNoteId,
  isPinning,
  pinningNoteId,
}: NoteProps<AllNote>) => {
  const quillRefItem = useRef(null);

  const getSourceLink = (note: AllNote) => {
    switch (note.source as SourceType) {
      case SOURCE.COMPANY_GROUP:
        return `/admin/crm/${note.source_id}`;
      case SOURCE.RESOLVED_TICKETS:
      case SOURCE.UNRESOLVED_TICKETS:
        return `/admin/tickets?ticketUuid=${note.source_id}`;
      case SOURCE.ALL_FILINGS:
        return `/admin/filings/${note.source_id}`;
    }
  };

  if (isLoading && notes.length === 0)
    return (
      <div className="t-flex t-justify-center t-items-center t-h-32">
        <Loader size="small" />
      </div>
    );

  if (notes.length === 0) {
    return <NoNoteAvailable />;
  }

  return (
    <InfiniteScroll
      dataLength={notes.length}
      next={setPagination}
      hasMore={hasMore}
      loader={
        isLoading ? (
          <div className="t-flex t-justify-center t-items-center t-my-4">
            <Loader size="small" />
          </div>
        ) : null
      }
      scrollableTarget="scrollableNotesDiv"
      className="t-w-full"
    >
      <div className="t-space-y-4">
        {notes.map((note, index) => {
          if (note.uuid === editableNoteId) {
            return (
              <EditNote
                noteId={note.uuid}
                description={note.note}
                setEditableNoteId={setEditableNoteId}
                key={note.uuid}
                resetNotes={resetNotes}
              />
            );
          }
          return (
            <div
              key={note.note + note.source_id}
              className={classNames(
                "t-py-4 t-px-4 t-rounded-lg t-border t-border-solid t-relative t-group t-flex t-gap-8 t-flex-col",
                {
                  "t-border-purple-10 t-bg-purple-0": note.is_pinned,
                  "t-border-neutral-0 t-bg-surface-lighter-grey":
                    !note.is_pinned,
                }
              )}
            >
              <div className="t-flex t-gap-1 t-items-start">
                <div className="t-flex-1">
                  <RenderDescription
                    description={note.note}
                    quillRefItem={quillRefItem}
                  />
                </div>
                {note.source === SOURCE.RESOLVED_TICKETS && (
                  <div className="t-h-10 t-flex t-items-center">
                    <ToolTip text="Ticket resolved">
                      <span>
                        <GreentickGreenCircle className="t-text-green-70 t-flex-shrink-0" />
                      </span>
                    </ToolTip>
                  </div>
                )}
                <NoteActions
                  noteId={note.uuid}
                  isPinned={note.is_pinned}
                  handlePinUpdate={handlePinUpdate}
                  isArchived={isArchived}
                  setEditableNoteId={setEditableNoteId}
                  resetNotes={resetNotes}
                  isPinning={isPinning && pinningNoteId === note.uuid}
                  isPinnedNote={note.is_pinned}
                  noteCreatedBy={note.created_by?.uuid}
                />
              </div>

              <div className="t-flex t-justify-between t-items-center">
                <div className="t-flex t-justify-between t-items-center">
                  <div className="t-text-body-sm t-text-text-30 t-flex t-gap-1.5 t-items-center">
                    <span>{note.created_by?.name || ""}</span>
                    <span>•</span>
                    {note.assignee && note.assignee.length > 0 && (
                      <>
                        <span>
                          Assigned to{" "}
                          {note.assignee
                            .map((assignee) => assignee.assignee_name)
                            .join(", ")}
                        </span>
                        <span>•</span>
                      </>
                    )}
                    <span>{formatDate(note.created_at)}</span>
                    {note.is_edited && (
                      <span className="t-text-body-sm t-italic">(Edited)</span>
                    )}
                  </div>
                </div>
                <ConditionalLink to={getSourceLink(note)} target="_blank">
                  <ToolTip text={note.source_name}>
                    <span className="t-text-body-sm t-text-text-30 t-ml-1 hover:t-text-purple hover:t-underline t-flex t-items-center t-gap-1 t-italic">
                      Source:
                      <div className="t-truncate t-max-w-[120px]">
                        {note.source_name}
                      </div>
                      <ArrowRightUpV2 className="t-size-2" />
                    </span>
                  </ToolTip>
                </ConditionalLink>
              </div>
            </div>
          );
        })}
      </div>
    </InfiniteScroll>
  );
};

export const UnifyNotes: React.FC<UnifyNotesProps> = ({
  isOpen,
  close,
  currentNoteLabel = "Notes for this",
  showSource = true,
  groupId,
  currentNoteType,
  entityId,
  currentNoteTypeId,
  ticketAssignees,
  ticketResolved,
}) => {
  const [activeTab, setActiveTab] = useState<NoteTabType>(NOTE_TAB.CURRENT);
  const [notesPayload, setNotesPayload] = useState({});
  const { alertToast, successToast } = useToast();
  const [pinNote, { isLoading: isPinning, originalArgs }] =
    usePinNoteMutation();
  const [addGroupNotes, { isLoading: isAddingGroupNote }] =
    useAddGroupNotesMutation();
  const [createNote, { isLoading: isAddingNote }] = useCreateNotesMutation();
  const [isArchived, setIsArchived] = useState(false);
  const [editableNoteId, setEditableNoteId] = useState<string | null>(null);

  const isTicketNote = currentNoteType === NOTE_TYPE.TICKETS;
  const { data: serviceTeamMembers = [] } = useGetServiceTeamQuery(
    {},
    { skip: !isTicketNote }
  );
  const mentionCandidates = isTicketNote
    ? serviceTeamMembers.map(({ uuid, name }) => ({ id: uuid, value: name }))
    : undefined;

  const {
    data: unifyNotesData,
    loadNext: setAllNotesPagination,
    pageNum: allNotesPageNum,
    isLoading: isUnifyNotesLoading,
    isFetching: isUnifyNotesFetching,
    reset: resetUnifyNotes,
  } = usePaginatedQuery<UnifyNotesApiResponse>(
    useLazyGetAllUnifyNotesQuery,
    "all_notes",
    { groupId, isArchived },
    ["groupId"]
  );

  const {
    data: groupNotesData,
    loadNext: setGroupNotePagination,
    pageNum: groupNotesPageNum,
    isLoading: isGroupNotesLoading,
    isFetching: isGroupNotesFetching,
    reset: resetGroupNotes,
  } = usePaginatedQuery<groupNotesResponse>(
    useLazyGroupNotesQuery,
    "notes",
    {
      groupId,
      isArchived,
    },
    ["groupId"],
    currentNoteType !== NOTE_TYPE.GROUP
  );

  const {
    data: notesData,
    loadNext: setNotesPagination,
    pageNum: notesPageNum,
    isLoading: isNotesLoading,
    isFetching: isNotesFetching,
    reset: resetCurrentNotes,
  } = usePaginatedQuery<NoteResponse>(
    useLazyGetNoteQuery,
    "comments",
    {
      entityId,
      parentObjId: currentNoteTypeId,
      isArchived,
    },
    ["entityId", "parentObjId"],
    !(
      currentNoteType === NOTE_TYPE.TASK ||
      currentNoteType === NOTE_TYPE.TICKETS
    )
  );

  const addGroupNote = async () => {
    try {
      await addGroupNotes({
        groupId,
        payload: { description: JSON.stringify(notesPayload) },
      }).unwrap();
      resetNotes();
      successToast({ message: "Note added successfully" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const addNote = async (comment_type: Note["comment_type"]) => {
    try {
      const taggedProfileIds = extractMentionedProfileIds(
        notesPayload as Parameters<typeof extractMentionedProfileIds>[0]
      );
      await createNote({
        entityId: entityId ?? "",
        parentObjId: currentNoteTypeId ?? "",
        payload: {
          description: JSON.stringify(notesPayload),
          comment_type: comment_type,
          ...(taggedProfileIds.length > 0
            ? { tagged_profile_ids: taggedProfileIds }
            : {}),
        },
      }).unwrap();
      resetNotes();
      successToast({
        message: "Note added successfully!",
      });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleAddNote = async () => {
    if (currentNoteType === NOTE_TYPE.GROUP) await addGroupNote();
    else if (currentNoteType === NOTE_TYPE.TASK) {
      await addNote("TASK_COMMENT" as Note["comment_type"]);
    } else if (currentNoteType === NOTE_TYPE.TICKETS) {
      await addNote("TICKET_COMMENT" as Note["comment_type"]);
    }
    setNotesPayload({});
    quillRef?.current?.setContents(new (Quill.import("delta"))());
  };

  const resetNotes = () => {
    resetUnifyNotes();
    if (currentNoteType === NOTE_TYPE.GROUP) resetGroupNotes();
    if (
      currentNoteType === NOTE_TYPE.TICKETS ||
      currentNoteType === NOTE_TYPE.TASK
    )
      resetCurrentNotes();
  };

  const handlePinUpdate = (noteId: string, is_pinned: boolean) => async () => {
    try {
      await pinNote({ noteId, is_pinned }).unwrap();
      resetNotes();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const setCurrentNotePagination = () => {
    switch (currentNoteType) {
      case NOTE_TYPE.TASK:
        return setNotesPagination;
      case NOTE_TYPE.TICKETS:
        return setNotesPagination;
      case NOTE_TYPE.GROUP:
        return setGroupNotePagination;
    }
  };

  const currentNotesData =
    currentNoteType === NOTE_TYPE.GROUP
      ? groupNotesData?.notes || []
      : notesData?.comments || [];

  const isCurrentNotesLoading =
    currentNoteType === NOTE_TYPE.GROUP ? isGroupNotesLoading : isNotesLoading;

  const isCurrentNotesFetching =
    currentNoteType === NOTE_TYPE.GROUP
      ? isGroupNotesFetching
      : isNotesFetching;

  const hasMoreCurrentNote =
    currentNoteType === NOTE_TYPE.GROUP
      ? groupNotesPageNum < (groupNotesData?.total_pages || 0)
      : notesPageNum < (notesData?.total_pages || 0);

  const currentNoteTotalCount =
    currentNoteType === NOTE_TYPE.GROUP
      ? groupNotesData?.total_count || 0
      : notesData?.total_count || 0;

  const quillRef = useRef<Quill | null>(null);

  const sharedProps = {
    handlePinUpdate,
    resetNotes,
    isArchived,
    editableNoteId,
    setEditableNoteId,
    isPinning,
    pinningNoteId: originalArgs?.noteId || null,
  };

  const isCurrentNoteTypeIdMissing = !currentNoteTypeId;
  const isQuillEditorEmpty = !quillRef?.current?.getText().trim();
  const isNotesPayloadEmpty = Object.entries(notesPayload || {}).length === 0;
  const isNoteBeingAdded = isAddingNote || isAddingGroupNote;
  const isNoteBeingEdited = Boolean(editableNoteId);

  const isSaveDisabled =
    isCurrentNoteTypeIdMissing ||
    isQuillEditorEmpty ||
    isNotesPayloadEmpty ||
    isNoteBeingAdded ||
    isNoteBeingEdited;

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content
        size="large"
        useCustomOverlay
        onPointerDownOutside={handleMentionListPointerDownOutside}
      >
        <Modal.Header>
          <Modal.Title>Notes</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-overflow-y-hidden">
          <div className="t-flex t-h-[400px] t-flex-col t-gap-4">
            <div className="t-top-0 t-bg-surface t-flex t-justify-between t-items-center">
              <ToggleGroup
                value={activeTab.toString()}
                onValueChange={(value) => setActiveTab(value as NoteTabType)}
                className="t-w-[60%]"
                disabled={Boolean(editableNoteId)}
              >
                <ToggleGroupItem value={NOTE_TAB.CURRENT}>
                  <>
                    {currentNoteLabel}
                    {currentNoteTotalCount > 0 && (
                      <span className="t-ml-1">({currentNoteTotalCount})</span>
                    )}
                  </>
                </ToggleGroupItem>
                <ToggleGroupItem value={NOTE_TAB.ALL}>
                  <>
                    All Notes{" "}
                    {(unifyNotesData?.total_count ?? 0) > 0 && (
                      <span className="t-ml-1">
                        ({unifyNotesData?.total_count})
                      </span>
                    )}
                  </>
                </ToggleGroupItem>
              </ToggleGroup>
              <Checkbox
                onChange={(e) => setIsArchived(e.target.checked)}
                checked={isArchived}
                label="View archived notes only"
                disabled={Boolean(editableNoteId)}
                name="archivedNotesCheckbox"
              />
            </div>
            <div
              className="t-flex-1 t-overflow-y-auto t-max-w-full t-overflow-x-hidden"
              id="scrollableNotesDiv"
            >
              {activeTab === NOTE_TAB.ALL && (
                <RenderAllNotes
                  notes={unifyNotesData?.all_notes || []}
                  setPagination={setAllNotesPagination}
                  isLoading={isUnifyNotesFetching || isUnifyNotesLoading}
                  hasMore={allNotesPageNum < (unifyNotesData?.total_pages || 0)}
                  {...sharedProps}
                />
              )}
              {activeTab === NOTE_TAB.CURRENT && (
                <RenderNotes
                  notes={currentNotesData}
                  setPagination={setCurrentNotePagination()}
                  isLoading={isCurrentNotesFetching || isCurrentNotesLoading}
                  hasMore={hasMoreCurrentNote}
                  currentNoteTypeId={currentNoteTypeId || ""}
                  ticketAssignees={ticketAssignees}
                  ticketResolved={ticketResolved}
                  mentionCandidates={mentionCandidates}
                  {...sharedProps}
                />
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-flex-col t-gap-2">
            <Suspense fallback={<Loader size="small" />}>
              <QuillEditor
                className="t-max-h-80 t-overflow-y-auto"
                parentRef={quillRef}
                onTextChange={(update) => {
                  // @ts-ignore
                  setNotesPayload(quillRef?.current?.getContents() || {});
                }}
                disabled={isNoteBeingEdited}
                mentionCandidates={mentionCandidates}
              />
            </Suspense>
            <div className="t-flex t-w-full t-justify-end t-gap-3  t-items-center">
              <Button
                customType="primary"
                isLoading={isNoteBeingAdded}
                disabled={isSaveDisabled}
                onClick={handleAddNote}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
